import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Settings, Clock, Save, RefreshCw, Shield, Bell, Users,
  Building2, AlertCircle, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { settingsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const officeHoursSchema = z.object({
  office_start_time: z.string().min(1, 'Start time is required'),
  office_end_time: z.string().min(1, 'End time is required'),
});

const passwordPolicySchema = z.object({
  min_length: z.number().min(4, 'Minimum length must be at least 4'),
  require_uppercase: z.boolean(),
  require_lowercase: z.boolean(),
  require_numbers: z.boolean(),
  require_special_chars: z.boolean(),
  max_age_days: z.number().min(1, 'Maximum age must be at least 1 day'),
  history_count: z.number().min(0, 'History count must be at least 0'),
  mfa_enabled: z.boolean(),
});

const SettingsManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [officeHours, setOfficeHours] = useState({});
  const [passwordPolicy, setPasswordPolicy] = useState({});
  const [activeTab, setActiveTab] = useState('general');
  const [refreshing, setRefreshing] = useState(false);

  const {
    control: officeControl,
    handleSubmit: handleOfficeSubmit,
    formState: { errors: officeErrors },
    reset: resetOffice,
  } = useForm({
    resolver: zodResolver(officeHoursSchema),
    defaultValues: {
      office_start_time: '09:00',
      office_end_time: '17:00',
    },
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordFormSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm({
    resolver: zodResolver(passwordPolicySchema),
    defaultValues: {
      min_length: 8,
      require_uppercase: true,
      require_lowercase: true,
      require_numbers: true,
      require_special_chars: true,
      max_age_days: 90,
      history_count: 5,
      mfa_enabled: false,
    },
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsData, officeHoursData, passwordPolicyData] = await Promise.all([
        settingsAPI.getSettings(),
        settingsAPI.getOfficeHours(),
        settingsAPI.getPasswordPolicy()
      ]);
      
      setSettings(settingsData.settings || {});
      setOfficeHours(officeHoursData);
      setPasswordPolicy(passwordPolicyData);
      
      if (officeHoursData) {
        resetOffice({
          office_start_time: officeHoursData.office_start_time || '09:00',
          office_end_time: officeHoursData.office_end_time || '17:00',
        });
      }
      
      if (passwordPolicyData) {
        resetPassword({
          min_length: passwordPolicyData.min_length || 8,
          require_uppercase: passwordPolicyData.require_uppercase !== false,
          require_lowercase: passwordPolicyData.require_lowercase !== false,
          require_numbers: passwordPolicyData.require_numbers !== false,
          require_special_chars: passwordPolicyData.require_special_chars !== false,
          max_age_days: passwordPolicyData.max_age_days || 90,
          history_count: passwordPolicyData.history_count || 5,
          mfa_enabled: passwordPolicyData.mfa_enabled || false,
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings', 'Please try again');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadSettings();
      toast.success('Settings refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh settings');
    } finally {
      setRefreshing(false);
    }
  };

  const handleOfficeHoursSubmit = async (data) => {
    setLoading(true);
    try {
      await settingsAPI.updateOfficeHours({
        office_start_time: data.office_start_time,
        office_end_time: data.office_end_time,
      });
      
      toast.success('Office hours updated successfully!');
      await loadSettings();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update office hours';
      toast.error('Failed to update office hours', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (data) => {
    setLoading(true);
    try {
      await settingsAPI.updatePasswordPolicy(data);
      
      toast.success('Password policy updated successfully!');
      await loadSettings();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update password policy';
      toast.error('Failed to update password policy', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingUpdate = async (key, value) => {
    try {
      await settingsAPI.updateSetting(key, value);
      toast.success('Setting updated successfully!');
      await loadSettings();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update setting';
      toast.error('Failed to update setting', errorMessage);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'office', label: 'Office Hours', icon: Clock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 mt-2">Manage system settings and preferences</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing}>
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card>
          <CardContent className="p-0">
            <div className="flex border-b border-gray-200">
              {tabs.map((tab, index) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-4 py-3 border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon size={16} className="mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {activeTab === 'general' && (
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Organization Name</Label>
                <Input
                  defaultValue={settings.organization_name || 'Food For Life Nepal'}
                  onBlur={(e) => handleSettingUpdate('organization_name', e.target.value)}
                />
              </div>
              
              <div>
                <Label>Default Language</Label>
                <Select
                  defaultValue={settings.default_language || 'en'}
                  onChange={(e) => handleSettingUpdate('default_language', e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="ne">Nepali</option>
                </Select>
              </div>
              
              <div>
                <Label>Date Format</Label>
                <Select
                  defaultValue={settings.date_format || 'YYYY-MM-DD'}
                  onChange={(e) => handleSettingUpdate('date_format', e.target.value)}
                >
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                </Select>
              </div>
              
              <div>
                <Label>Currency</Label>
                <Select
                  defaultValue={settings.currency || 'NPR'}
                  onChange={(e) => handleSettingUpdate('currency', e.target.value)}
                >
                  <option value="NPR">NPR (Nepali Rupee)</option>
                  <option value="USD">USD (US Dollar)</option>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'office' && (
          <Card>
            <CardHeader>
              <CardTitle>Office Hours Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleOfficeHoursSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="office_start_time">Start Time</Label>
                    <Controller
                      name="office_start_time"
                      control={officeControl}
                      render={({ field }) => (
                        <Input
                          type="time"
                          {...field}
                          error={officeErrors.office_start_time?.message}
                        />
                      )}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="office_end_time">End Time</Label>
                    <Controller
                      name="office_end_time"
                      control={officeControl}
                      render={({ field }) => (
                        <Input
                          type="time"
                          {...field}
                          error={officeErrors.office_end_time?.message}
                        />
                      )}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    loading={loading}
                    className="min-w-32"
                  >
                    <Save size={16} className="mr-2" />
                    Save Office Hours
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {activeTab === 'notifications' && (
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-gray-500">Receive email notifications for important updates</p>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked={settings.email_notifications !== false}
                    onChange={(e) => handleSettingUpdate('email_notifications', e.target.checked)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Task Reminders</p>
                    <p className="text-sm text-gray-500">Get reminded about upcoming task deadlines</p>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked={settings.task_reminders !== false}
                    onChange={(e) => handleSettingUpdate('task_reminders', e.target.checked)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Attendance Alerts</p>
                    <p className="text-sm text-gray-500">Alert when staff miss clock-in/out</p>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked={settings.attendance_alerts !== false}
                    onChange={(e) => handleSettingUpdate('attendance_alerts', e.target.checked)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Meal Request Updates</p>
                    <p className="text-sm text-gray-500">Notifications for meal request status changes</p>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked={settings.meal_request_updates !== false}
                    onChange={(e) => handleSettingUpdate('meal_request_updates', e.target.checked)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'security' && (
          <Card>
            <CardHeader>
              <CardTitle>Password Policy Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordFormSubmit(handlePasswordSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min_length">Minimum Length</Label>
                    <Controller
                      name="min_length"
                      control={passwordControl}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min="4"
                          max="128"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          error={passwordErrors.min_length?.message}
                        />
                      )}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="max_age_days">Maximum Age (days)</Label>
                    <Controller
                      name="max_age_days"
                      control={passwordControl}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          error={passwordErrors.max_age_days?.message}
                        />
                      )}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="history_count">Password History Count</Label>
                    <Controller
                      name="history_count"
                      control={passwordControl}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min="0"
                          max="24"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          error={passwordErrors.history_count?.message}
                        />
                      )}
                    />
                    <p className="text-sm text-gray-500 mt-1">Number of previous passwords to remember</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Password Requirements</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Require Uppercase Letters</p>
                        <p className="text-sm text-gray-500">Password must contain at least one uppercase letter (A-Z)</p>
                      </div>
                      <Controller
                        name="require_uppercase"
                        control={passwordControl}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        )}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Require Lowercase Letters</p>
                        <p className="text-sm text-gray-500">Password must contain at least one lowercase letter (a-z)</p>
                      </div>
                      <Controller
                        name="require_lowercase"
                        control={passwordControl}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        )}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Require Numbers</p>
                        <p className="text-sm text-gray-500">Password must contain at least one number (0-9)</p>
                      </div>
                      <Controller
                        name="require_numbers"
                        control={passwordControl}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        )}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Require Special Characters</p>
                        <p className="text-sm text-gray-500">Password must contain at least one special character (!@#$%^&*)</p>
                      </div>
                      <Controller
                        name="require_special_chars"
                        control={passwordControl}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        )}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Multi-Factor Authentication</p>
                        <p className="text-sm text-gray-500">Require MFA for all users</p>
                      </div>
                      <Controller
                        name="mfa_enabled"
                        control={passwordControl}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="text-blue-600 mt-0.5 mr-2" size={16} />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Password Policy Information</p>
                      <p>These settings determine password requirements for all users. Changes will apply to new passwords and password resets.</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    loading={loading}
                    className="min-w-32"
                  >
                    <Save size={16} className="mr-2" />
                    Save Password Policy
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </motion.div>

      {/* User Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Logged in as</p>
                <p className="font-medium">{user?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {user?.roles?.map((role, index) => (
                    <Badge key={index} variant="info">
                      {role.role_name}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Branch</p>
                <p className="font-medium">{user?.branch_id || 'All Branches'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default SettingsManagement;
